import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { Avatar } from "../primitives/Avatar";
import { IconBtn } from "../primitives/IconBtn";
import { useIsMobile } from "../primitives/useIsMobile";
import { Breadcrumbs } from "./Breadcrumbs";
import { GlobalSearch } from "./GlobalSearch";
import { ClockPill } from "./ClockPill";
import { NotificacionesPanel } from "./NotificacionesPanel";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { useAvatarBlobUrl } from "../../helpers/useAvatarBlobUrl";
import { ME_AVATAR } from "../../helpers/url_helper";
import { useLocation } from "react-router-dom";
import { useTour } from "../../tours/TourContext";
import { TOUR_LABELS, tourKeyForPath } from "../../tours/tours";
import {
  clearAuthorization,
} from "../../helpers/api_helper";
import { getNotificationsUnreadCount, postLogout } from "../../helpers/kronaly_helper";

const NOTIF_POLL_MS = 60000;

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

export function Topbar({ showHamburger = false, onOpenMobileNav }) {
  const isMobile = useIsMobile();
  const { me, role } = useCurrentMembership();
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [hasAvatarOverride, setHasAvatarOverride] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setHasAvatarOverride(e.detail?.has_avatar ?? null);
      setAvatarVersion((v) => v + 1);
    };
    window.addEventListener("kronaly:avatar-changed", handler);
    return () => window.removeEventListener("kronaly:avatar-changed", handler);
  }, []);

  const hasAvatar = hasAvatarOverride !== null ? hasAvatarOverride : me?.has_avatar;
  const { blobUrl: myAvatarUrl } = useAvatarBlobUrl(
    hasAvatar ? ME_AVATAR : null,
    { enabled: !!hasAvatar, version: avatarVersion },
  );
  const location = useLocation();
  const { replayTour } = useTour();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpTourKey = tourKeyForPath(location.pathname);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const refreshUnread = useCallback(async () => {
    try {
      const r = await getNotificationsUnreadCount();
      const payload = unwrap(r) || {};
      const c = Number(payload.unread_count ?? payload.count ?? 0);
      setUnreadCount(Number.isFinite(c) ? c : 0);
    } catch (_) {
      // silencioso: si falla el conteo no rompemos la UI
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    pollRef.current = setInterval(refreshUnread, NOTIF_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshUnread();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshUnread]);

  const onLogout = async () => {
    try {
      await postLogout({});
    } catch (_) {
      /* silencioso */
    } finally {
      localStorage.removeItem("authUser");
      localStorage.removeItem("kronaly_workspace_id");
      clearAuthorization();
      navigate("/login");
    }
  };

  return (
    <header
      style={{
        height: 56,
        background: "var(--kr-bg-surface)",
        borderBottom: "1px solid var(--kr-border)",
        display: "flex",
        alignItems: "center",
        padding: isMobile ? "0 12px" : "0 16px",
        gap: isMobile ? 8 : 12,
        flexShrink: 0,
      }}
    >
      {showHamburger && (
        <IconBtn
          icon="Menu"
          title="Menú"
          aria-label="Abrir menú"
          onClick={onOpenMobileNav}
        />
      )}
      <Breadcrumbs />
      <div style={{ flex: 1 }} />
      {!isMobile && <GlobalSearch />}
      <ClockPill />
      {!isMobile && (
        <>
          <div
            style={{
              width: 1,
              height: 22,
              background: "var(--kr-border)",
              margin: "0 4px",
            }}
          />
          <HelpMenu
            open={helpOpen}
            onToggle={() => setHelpOpen((v) => !v)}
            onClose={() => setHelpOpen(false)}
            tourKey={helpTourKey}
            onReplayTour={(key) => {
              setHelpOpen(false);
              replayTour(key);
            }}
          />
        </>
      )}
      <IconBtn
        icon="Bell"
        title="Notificaciones"
        badge={unreadCount}
        onClick={() => setNotifOpen(true)}
      />
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setProfileOpen((v) => !v)}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            marginLeft: 4,
            cursor: "pointer",
          }}
          title={me?.name || "Cuenta"}
        >
          <Avatar
            name={me?.name || "?"}
            size={28}
            status="working"
            src={myAvatarUrl || undefined}
          />
        </button>
        {profileOpen && (
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "100%",
              marginTop: 6,
              background: "var(--kr-bg-surface)",
              border: "1px solid var(--kr-border)",
              borderRadius: 8,
              boxShadow: "var(--kr-shadow-md)",
              padding: 6,
              minWidth: 220,
              zIndex: 30,
            }}
          >
            <div
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid var(--kr-border-subtle)",
                marginBottom: 4,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
                {me?.name || "Usuario"}
              </div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                {me?.email || "—"}
              </div>
              {role && (
                <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
                  Rol: {role}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setProfileOpen(false);
                navigate("/configuracion");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 10px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--kr-fg-primary)",
                fontSize: 13,
                borderRadius: 6,
                textAlign: "left",
              }}
            >
              <Icon name="Settings" size={14} style={{ color: "var(--kr-fg-muted)" }} />
              Configuración
            </button>
            <button
              type="button"
              onClick={onLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 10px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--kr-danger-700)",
                fontSize: 13,
                borderRadius: 6,
                textAlign: "left",
              }}
            >
              <Icon name="LogOut" size={14} />
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
      <NotificacionesPanel
        open={notifOpen}
        onClose={() => {
          setNotifOpen(false);
          refreshUnread();
        }}
        onUpdated={refreshUnread}
      />
    </header>
  );
}

/**
 * Botón de ayuda + dropdown que ofrece reproducir el tour de la página
 * actual (si la página tiene tour definido) y, eventualmente, otras
 * acciones de ayuda (docs, soporte). Solo desktop por ahora.
 */
function HelpMenu({ open, onToggle, onClose, tourKey, onReplayTour }) {
  return (
    <div style={{ position: "relative" }}>
      <IconBtn icon="HelpCircle" title="Ayuda" onClick={onToggle} />
      {open && (
        <>
          {/* Backdrop transparente para cerrar al click fuera */}
          <div
            onClick={onClose}
            style={{ position: "fixed", inset: 0, zIndex: 29 }}
          />
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "100%",
              marginTop: 6,
              background: "var(--kr-bg-surface)",
              border: "1px solid var(--kr-border)",
              borderRadius: 8,
              boxShadow: "var(--kr-shadow-md)",
              padding: 6,
              minWidth: 240,
              zIndex: 30,
            }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--kr-fg-muted)",
                fontWeight: 600,
                padding: "6px 10px 4px",
              }}
            >
              Ayuda
            </div>
            {tourKey ? (
              <button
                type="button"
                onClick={() => onReplayTour(tourKey)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--kr-fg-primary)",
                  fontSize: 13,
                  borderRadius: 6,
                  textAlign: "left",
                }}
              >
                <Icon name="PlayCircle" size={14} style={{ color: "var(--kr-fg-muted)" }} />
                Reproducir tour de "{TOUR_LABELS[tourKey] || "esta página"}"
              </button>
            ) : (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: "8px 10px" }}>
                Esta página no tiene tour disponible.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Topbar;
